import Vue from 'vue'
import VueRouter from 'vue-router'
import { isUserLoggedIn } from '@/auth/utils'
import useJwt from '@/auth/jwt/useJwt'
import i18n from '@/libs/i18n'
import {
  DEFAULT_LOCALE,
  ensureStoredLocale,
  syncI18nLocale,
} from '@/libs/i18n/currentLocale'

Vue.use(VueRouter)

const router = new VueRouter({
  mode: 'history',
  // base: process.env.BASE_URL,
  // scrollBehavior() {
  //     return {x: 0, y: 0};
  // },
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/Home.vue'),
      meta: {
        layout: 'fullIndex',
        defaultRoute: true,
      },
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('@/views/About.vue'),
      meta: {
        layout: 'fullIndex',
        defaultRoute: true,
      },
    },
    {
      path: '/integrations/fortnox',
      name: 'integration-fortnox',
      component: () => import('@/views/IntegrationFortnox.vue'),
      meta: {
        layout: 'fullIndex',
        defaultRoute: true,
      },
    },
    {
      path: '/integrations/spiris',
      name: 'integration-spiris',
      component: () => import('@/views/IntegrationSpiris.vue'),
      meta: {
        layout: 'fullIndex',
        defaultRoute: true,
      },
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import('@/views/Contact.vue'),
      meta: {
        layout: 'fullIndex',
        defaultRoute: true,
      },
    },
    {
      path: '/blog',
      name: 'blog',
      component: () => import('@/views/Blog.vue'),
      meta: {
        layout: 'fullIndex',
        defaultRoute: true,
      },
    },
    {
      path: '/blog/:slug',
      name: 'blogdetail',
      component: () => import('@/views/BlogDetail.vue'),
      meta: {
        blogDetails: true,
        layout: 'fullIndex',
        defaultRoute: true,
      },
    },
    {
      path: '/legal/cookies',
      name: 'cookies',
      component: () => import('@/views/Cookies.vue'),
      meta: {
        layout: 'fullIndex',
        defaultRoute: true,
      },
    },

    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/views/Dashboard.vue'),
      meta: {
        pageTitle: 'Sidebar.Dashboard',
        redirectIfLoggedIn: true,
        breadcrumb: [
          {
            text: 'Sidebar.Dashboard',
            active: true,
          },
        ],
      },
    },
    {
      path: '/dashboard/settings',
      name: 'settings',
      component: () => import('@/views/Settings.vue'),
      meta: {
        pageTitle: 'Sidebar.Settings',
        redirectIfLoggedIn: true,
        breadcrumb: [
          {
            text: 'Sidebar.Settings',
            active: true,
          },
        ],
      },
    },
    {
      path: '/dashboard/subscription',
      name: 'subscription',
      component: () => import('@/views/Subscription.vue'),
      meta: {
        pageTitle: 'Sidebar.Subscription',
        redirectIfLoggedIn: true,
        breadcrumb: [
          {
            text: 'Sidebar.Subscription',
            active: true,
          },
        ],
      },
    },
    {
      path: '/dashboard/billing',
      name: 'billing',
      component: () => import('@/views/BillingPage.vue'),
      meta: {
        pageTitle: 'Sidebar.BillingPage',
        redirectIfLoggedIn: true,
        breadcrumb: [
          {
            text: 'Sidebar.BillingPage',
            active: true,
          },
        ],
      },
    },
    {
      path: '/dashboard/faq',
      name: 'faq',
      component: () => import('@/views/Faq.vue'),
      meta: {
        pageTitle: 'Sidebar.Faq',
        redirectIfLoggedIn: true,
        breadcrumb: [
          {
            text: 'Sidebar.Faq',
            active: true,
          },
        ],
      },
    },
    {
      path: '/dashboard/event-log',
      name: 'eventLog',
      component: () => import('@/views/EventLog.vue'),
      meta: {
        pageTitle: 'Sidebar.ErrorLog',
        redirectIfLoggedIn: true,
        breadcrumb: [
          {
            text: 'Sidebar.ErrorLog',
            active: true,
            // icon: '',
          },
        ],
      },
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/Login.vue'),
      meta: {
        mainRoute: true,
        defaultRoute: true,
        layout: 'full',
      },
    },
    {
      path: '/signup',
      name: 'signup',
      component: () => import('@/views/Signup.vue'),
      meta: {
        mainRoute: true,
        defaultRoute: true,
        layout: 'full',
      },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/views/ForgotPassword.vue'),
      meta: {
        mainRoute: true,
        defaultRoute: true,
        layout: 'full',
      },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPassword.vue'),
      meta: {
        resetPassword: true,
        defaultRoute: true,
        mainRoute: true,
        layout: 'full',
      },
    },
    {
      path: '/error-404',
      name: 'error-404',
      component: () => import('@/views/error/Error404.vue'),
      meta: {
        layout: 'full',
        defaultRoute: true,
      },
    },
    {
      path: '*',
      redirect: 'error-404',
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  const lang = ensureStoredLocale()

  // Main lang, Language for home / blog
  localStorage.setItem('mainLang', 'sv')

  const isLoggedIn = isUserLoggedIn()

  const goLogin = reason => {
    try {
      localStorage.removeItem('userData')
    } catch (e) {}
    return next({
      name: 'login',
      query: { q: reason || 'tokenExpired' },
    })
  }

  try {
    if (to.meta.redirectIfLoggedIn) {
      if (!isLoggedIn) return next('/login')

      syncI18nLocale(i18n, lang)

      // Verify session/token with backend before allowing access
      try {
        const res = await useJwt.stats()
        if (res && res.status === 401) return goLogin('tokenExpired')
      } catch (err) {
        const status = err && err.response && err.response.status
        if (status === 401 || status === 403) return goLogin('tokenExpired')
      }
      return next()
    } if (to.meta.mainRoute) {
      // login/signup/forgot/reset pages
      if (!isLoggedIn) return next()

      // If explicitly coming with a reason, allow showing auth page
      if (to.query && to.query.q) return next()

      // Validate session; if invalid, stay on login
      try {
        const res = await useJwt.stats()
        if (res && res.status === 401) return next()
      } catch (err) {
        const status = err && err.response && err.response.status
        if (status === 401 || status === 403) return next()
      }
      return next('/dashboard')
    }

    if (to.meta.defaultRoute) {
      syncI18nLocale(i18n, localStorage.getItem('mainLang') || DEFAULT_LOCALE)
    }

    return next()
  } catch (err) {
    // On unexpected errors, fail safe to login
    return goLogin('tokenExpired')
  }
})

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach((to, from, failure) => {
  // Check
  ensureStoredLocale()

  if (to.meta.blogDetails) {
    useJwt
      .blogDetails(to.params.slug, {
        transformRequest: (data, headers) => {
          delete headers.Authorization
          return data
        },
      })
      .catch(err => {
        if (err.response) router.push({ name: 'error-404' })
      })
  }

  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) appLoading.style.display = 'none'

  // return next();
})

export default router
